import React, { useState, useEffect } from 'react';
import styled from 'styled-components';

import { Timer } from 'src/assets/timer';
import { calculateTimeRemaining, formatTimeDisplay } from 'src/utils/time-remaining';

type OutletCollectionTimerMessageSize = 'default' | 'small';

type OutletCollectionTimerMessageProps = {
  endDate: string;
  message?: string;
  size?: OutletCollectionTimerMessageSize;
};

const sizeConfig = {
  small: {
    fontSize: 12,
    iconSize: 14,
    gap: 4,
  },
  default: {
    fontSize: 16,
    iconSize: 20,
    gap: 8,
  },
};

/**
 * OutletCollectionTimerMessage displays an inline countdown timer for outlet collections.
 * Shows a timer icon with a message and countdown (e.g., "Order soon. Outlet closes in 02:24:34").
 *
 * This component is designed to be reusable across the PDP, cart drawer, and checkout pages.
 */
export function OutletCollectionTimerMessage({
  endDate,
  message = 'Order soon. Outlet closes in',
  size = 'default',
}: OutletCollectionTimerMessageProps): JSX.Element | null {
  const config = sizeConfig[size];
  const [timeRemaining, setTimeRemaining] = useState(() => calculateTimeRemaining(endDate));

  useEffect(() => {
    const interval = setInterval(() => {
      setTimeRemaining(calculateTimeRemaining(endDate));
    }, 1000);

    return () => clearInterval(interval);
  }, [endDate]);

  // Don't render if timer has expired
  if (timeRemaining.isExpired) {
    return null;
  }

  const timeDisplay = formatTimeDisplay(timeRemaining);

  return (
    <Container gap={config.gap} data-testid='outlet-collection-timer-message'>
      <TimerIcon size={config.iconSize} />
      <TimerText size={config.fontSize}>
        {message} {timeDisplay}
      </TimerText>
    </Container>
  );
}

const Container = styled.div<{ gap: number }>`
  display: flex;
  align-items: center;
  gap: ${({ gap }) => gap}px;
`;

const TimerIcon = styled(Timer)<{ size: number }>`
  flex-shrink: 0;
  width: ${({ size }) => size}px;
  height: ${({ size }) => size}px;
  color: #d55454;
`;

const TimerText = styled.span<{ size: number }>`
  font-family: var(--dct--customized-fonts-secondary);
  font-size: ${({ size }) => size}px;
  font-weight: 500;
  color: #d55454;
  line-height: normal;
  white-space: nowrap;
`;
